import type { NavItem } from '#engage-market-maker/utils/engage-a-market-maker'
import type { Component } from 'vue'
import { ArrowPathIcon, CheckCircleIcon, LockClosedIcon, LockOpenIcon, XCircleIcon } from '@heroicons/vue/24/outline'

export type RfqV2TabKey = 'rfq' | 'round-1' | 'round-2'

export interface RfqV2Tab {
  key: RfqV2TabKey
  label: string
  link: string
  to: string
  status: NavItem['status']
  icon: Component
  active: boolean
}

const TABS: { key: RfqV2TabKey, slug: string, label: string }[] = [
  { key: 'rfq', slug: 'request-for-quotations', label: '1. Request for Quotation' },
  { key: 'round-1', slug: 'received-quotes', label: 'Round 1: Received Quotes' },
  { key: 'round-2', slug: 'received-requotes', label: 'Round 2: Received Quotes' },
]

function iconForTab(key: RfqV2TabKey, status: NavItem['status']): Component {
  if (key === 'rfq') {
    return LockOpenIcon
  }

  switch (status) {
    case 'completed':
      return CheckCircleIcon
    case 'requoted':
      return ArrowPathIcon
    case 'cancelled':
      return XCircleIcon
    case 'available':
      return LockOpenIcon
    case 'lock':
    case 'none':
    default:
      return LockClosedIcon
  }
}

/**
 * Auth-safe wrapper for the rfq-v2 navigation. Logged out there is no project, so
 * `useRFQDetails` has no data and `navItems` falls back to default statuses (RFQ
 * unlocked, rounds locked) — surfaced here without ever requiring a project, so
 * the nav renders both authenticated and pre-login.
 */
export function useRfqV2Nav() {
  const auth = useAuth()
  const route = useRoute()
  const rfqDetails = useRFQDetails()

  const isLoggedIn = computed(() => auth.isLoggedIn)

  const tabs = computed<RfqV2Tab[]>(() =>
    TABS
      .map((tab): RfqV2Tab | undefined => {
        const item = rfqDetails.navItems.find(navItem => navItem.link.endsWith(`/${tab.slug}`))
        if (!item) {
          return undefined
        }
        return {
          key: tab.key,
          label: tab.label,
          link: item.link,
          // Authenticated users keep the /project/:projectId prefix; pre-login uses the bare path.
          to: auth.project ? getProjectPath(item.link) : item.link,
          status: item.status,
          icon: iconForTab(tab.key, item.status),
          active: route.path.endsWith(item.link),
        }
      })
      .filter((tab): tab is RfqV2Tab => Boolean(tab)),
  )

  return {
    isLoggedIn,
    tabs,
  }
}
